/**
 * Tipo de un archivo de la Biblioteca de Media.
 */
export type MediaType = 'video' | 'image'

/**
 * Representa un archivo (video o imagen) de la Biblioteca de Media.
 *
 * Los binarios viven en Firebase Storage bajo la ruta `media/{createdBy}/{timestamp}_{filename}`
 * y la metadata estructurada vive en la colección `media` de Firestore. El campo
 * {@link MediaItem.storagePath} apunta al blob en Storage para borrado/preview;
 * {@link MediaItem.url} es la URL pública de descarga generada al subir.
 *
 * Regla de propiedad: el creador (`createdBy === authUser.uid`) puede editar/eliminar;
 * adicionalmente, los emails en `mediaEmailException` (secreacademica/soporte) pueden
 * gestionar cualquier archivo. No hay overrides por rol — `dir`/`superadmin` solo
 * pueden actuar sobre lo propio. La regla canónica vive en `canManageMediaItem`
 * (`src/utils/permissions.ts`).
 */
export interface MediaItem {
  /** ID del documento en Firestore (colección `media`). */
  id: string
  /** Nombre legible asignado por el usuario al subir o al editar. */
  name: string
  /** URL pública de descarga generada por `getDownloadURL`. */
  url: string
  /** Ruta del blob en Storage, formato `media/{createdBy}/{timestamp}_{filename}`. */
  storagePath: string
  /** Clasificación derivada del MIME: `video` o `image`. */
  type: MediaType
  /** Tamaño en bytes. */
  fileSize: number
  /** MIME exacto del archivo subido (ej. `video/mp4`). */
  mimeType: string
  /** UID de Firebase Auth del creador. Inmutable. */
  createdBy: string
  /** Email del creador al momento de la subida. Identificador único legible. */
  createdByEmail: string
  /**
   * Nombre legible del creador al momento de la subida
   * (`displayName` con fallback a email/'Usuario Desconocido', siguiendo el patrón de `documents.lastEditor`).
   * Opcional por retro-compatibilidad con docs creados antes de su introducción.
   * TODO: hacer obligatorio cuando se confirme que todos los docs en la colección
   * `media` tienen este campo (vía backfill o porque la colección era nueva).
   */
  createdByName?: string
  /** Fecha de creación en ISO 8601. */
  createdAt: string
  /** Fecha de última actualización en ISO 8601 (presente tras editar el nombre). */
  updatedAt?: string
}

/** Tamaño máximo aceptado en MB. */
export const MAX_MEDIA_FILE_SIZE_MB = 100

/** Tamaño máximo aceptado en bytes (derivado de {@link MAX_MEDIA_FILE_SIZE_MB}). */
export const MAX_MEDIA_FILE_SIZE_BYTES = MAX_MEDIA_FILE_SIZE_MB * 1024 * 1024

/** MIME types aceptados para upload (videos e imágenes). */
export const ACCEPTED_MEDIA_TYPES: readonly string[] = [
  'video/mp4',
  'video/webm',
  'video/ogg',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
]

/**
 * Deriva el {@link MediaType} a partir del MIME del archivo.
 *
 * @param mimeType MIME del archivo (ej. `image/png`).
 * @returns `'video'` si el MIME empieza con `video/`, sino `'image'`.
 */
export function getMediaType(mimeType: string): MediaType {
  return mimeType.startsWith('video/') ? 'video' : 'image'
}

/**
 * Valida un archivo contra los límites de la Biblioteca de Media (tamaño + MIME).
 * Fuente de verdad única — usado por el thunk (boundary de seguridad) y el modal
 * (UX anticipada). El thunk se mantiene como la última línea de defensa.
 *
 * @returns mensaje de error en español si invalido, `null` si pasa todos los checks.
 */
export function validateMediaFile(file: File): string | null {
  if (file.size > MAX_MEDIA_FILE_SIZE_BYTES) {
    return `El archivo no puede superar ${MAX_MEDIA_FILE_SIZE_MB} MB`
  }
  if (!ACCEPTED_MEDIA_TYPES.includes(file.type)) {
    return `Tipo de archivo no aceptado. Permitidos: ${ACCEPTED_MEDIA_TYPES.join(', ')}.`
  }
  return null
}
